import { api } from "@glasshalf/api-client";
import type { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { DataTable } from "../../components/DataTable";
import { SavedViews, type SavedView } from "../../components/SavedViews";
import { StatusBadge } from "../../components/StatusBadge";
import { formatNumber, formatSizeLabel, normalizeText } from "../../lib/format";
import { productRows } from "../../lib/selectors";
import type { AppData, ProductRow, SkuRow } from "../../lib/types";

type ProductsPageProps = {
  data: AppData;
};

type ProductCreateFormState = {
  productName: string;
  seasonCode: string;
  categoryCode: string;
  colorways: string;
  sizes: string;
};

const initialCreateForm: ProductCreateFormState = {
  productName: "",
  seasonCode: "26SS",
  categoryCode: "TSHT",
  colorways: "",
  sizes: ""
};

export function ProductsPage({ data }: ProductsPageProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [expandedProductKey, setExpandedProductKey] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<ProductCreateFormState>(initialCreateForm);
  const products = productRows(data);
  const createProduct = useMutation({
    mutationFn: () => api.createProduct({
      productName: createForm.productName.trim(),
      seasonCode: createForm.seasonCode.trim().toUpperCase(),
      categoryCode: createForm.categoryCode.trim().toUpperCase(),
      colorways: parseOptionList(createForm.colorways).map((name) => ({ name })),
      sizes: parseOptionList(createForm.sizes).map((name) => ({ name, code: name }))
    }),
    onSuccess: async () => {
      setCreateForm(initialCreateForm);
      setCreateOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    }
  });
  const activeView = params.get("view") || "all";
  const views: SavedView[] = [
    { key: "all", label: "전체", count: products.length },
    { key: "needs_review", label: "확인 필요", count: products.filter((product) => product.channelStatus === "매핑 필요").length },
    { key: "content", label: "콘텐츠 확인", count: products.filter(productNeedsContentReview).length },
    { key: "unmapped", label: "Cafe24 미매핑", count: products.filter((product) => product.channelStatus === "매핑 필요").length },
    { key: "out", label: "재고 없음", count: products.filter((product) => product.onHandTotal <= 0).length }
  ];
  const filtered = products.filter((product) => {
    const text = normalizeText([product.productName, product.productKey, product.seasonCode, product.categoryName, product.channelStatus].join(" "));
    if (query && !text.includes(normalizeText(query))) return false;
    if (activeView === "needs_review") return product.channelStatus === "매핑 필요";
    if (activeView === "content") return productNeedsContentReview(product);
    if (activeView === "unmapped") return product.channelStatus === "매핑 필요";
    if (activeView === "out") return product.onHandTotal <= 0;
    return true;
  });
  const columns = useMemo<ColumnDef<ProductRow>[]>(() => [
    {
      accessorKey: "productName",
      header: "상품명",
      cell: ({ row }) => (
        <button
          type="button"
          className="table-link"
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/products/${encodeURIComponent(row.original.productKey)}`);
          }}
        >
          {row.original.productName}
        </button>
      ),
      size: 220
    },
    {
      accessorKey: "productKey",
      header: "내부 상품코드",
      cell: ({ row }) => row.original.productKey,
      size: 140
    },
    {
      accessorKey: "seasonCode",
      header: "시즌",
      cell: ({ row }) => row.original.seasonCode,
      size: 90
    },
    { accessorKey: "categoryName", header: "카테고리" },
    {
      accessorKey: "colorCount",
      header: "컬러",
      cell: ({ row }) => formatNumber(row.original.colorCount)
    },
    {
      accessorKey: "sizeCount",
      header: "사이즈",
      cell: ({ row }) => formatNumber(row.original.sizeCount)
    },
    {
      accessorKey: "skuCount",
      header: "SKU",
      cell: ({ row }) => formatNumber(row.original.skuCount)
    },
    {
      accessorKey: "onHandTotal",
      header: "실재고",
      cell: ({ row }) => <span className="num">{formatNumber(row.original.onHandTotal)}</span>
    },
    {
      accessorKey: "allocatedTotal",
      header: "온라인 할당",
      cell: ({ row }) => <span className="num">{formatNumber(row.original.allocatedTotal)}</span>
    },
    {
      accessorKey: "channelStatus",
      header: "채널",
      cell: ({ row }) => row.original.channelStatus === "매핑 필요" ? <StatusBadge tone="warning">매핑 필요</StatusBadge> : <StatusBadge tone="success">{row.original.channelStatus}</StatusBadge>
    }
  ], [navigate]);
  const canCreate = Boolean(
    createForm.productName.trim() &&
    createForm.seasonCode.trim() &&
    createForm.categoryCode.trim() &&
    parseOptionList(createForm.colorways).length &&
    parseOptionList(createForm.sizes).length
  );

  return (
    <div className="page-stack">
      <section className="toolbar-panel">
        <SavedViews views={views} active={activeView} onChange={(view) => setParams(view === "all" ? {} : { view })} />
        <div className="toolbar-row">
          <label className="search-field">
            검색
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="상품명, 상품코드, 시즌, 카테고리" />
          </label>
          <button type="button" className="primary" onClick={() => setCreateOpen((open) => !open)}>
            <Plus size={15} />
            상품 등록
          </button>
        </div>
      </section>
      {createOpen ? (
        <section className="panel product-create-panel">
          <div className="panel-head">
            <div>
              <h2>상품 등록</h2>
              <p>컬러와 사이즈를 기준으로 내부 SKU를 발급합니다. Cafe24 반영은 상품 상세에서 별도 승인 흐름으로 처리합니다.</p>
            </div>
          </div>
          <form
            className="product-create-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (canCreate) createProduct.mutate();
            }}
          >
            <div className="form-grid">
              <label>
                상품명
                <input
                  value={createForm.productName}
                  onChange={(event) => setCreateForm((form) => ({ ...form, productName: event.target.value }))}
                  placeholder="예: Garment Dyed Tee"
                />
              </label>
              <label>
                시즌
                <input
                  value={createForm.seasonCode}
                  onChange={(event) => setCreateForm((form) => ({ ...form, seasonCode: event.target.value }))}
                  placeholder="26SS"
                />
              </label>
              <label>
                카테고리 코드
                <input
                  value={createForm.categoryCode}
                  onChange={(event) => setCreateForm((form) => ({ ...form, categoryCode: event.target.value }))}
                  placeholder="TSHT"
                />
              </label>
              <label>
                사이즈
                <input
                  value={createForm.sizes}
                  onChange={(event) => setCreateForm((form) => ({ ...form, sizes: event.target.value }))}
                  placeholder="S, M, L, XL"
                />
              </label>
              <label className="span-2">
                컬러웨이
                <input
                  value={createForm.colorways}
                  onChange={(event) => setCreateForm((form) => ({ ...form, colorways: event.target.value }))}
                  placeholder="Faded Black, Dust Blue, Off White"
                />
              </label>
            </div>
            {createProduct.error ? <p className="form-error">{(createProduct.error as Error).message}</p> : null}
            <div className="form-actions">
              <button type="button" onClick={() => setCreateOpen(false)}>취소</button>
              <button type="submit" className="primary" disabled={!canCreate || createProduct.isPending}>
                {createProduct.isPending ? "등록 중" : "상품 등록"}
              </button>
            </div>
          </form>
        </section>
      ) : null}
      <section className="panel">
        <DataTable
          columns={columns}
          data={filtered}
          getRowId={(row) => row.productKey}
          selectedId={expandedProductKey || undefined}
          onSelect={(record) => {
            const nextProductKey = expandedProductKey === record.productKey ? null : record.productKey;
            setExpandedProductKey(nextProductKey);
          }}
          renderExpandedRow={(record) => <ProductSkuPreview product={record} />}
          emptyTitle="표시할 상품이 없습니다."
        />
      </section>
    </div>
  );
}

function ProductSkuPreview({ product }: { product: ProductRow }) {
  const rows = sortedSkuRows(product.rows);
  return (
    <div className="products-sku-preview">
      <div className="products-sku-preview-head">
        <div>
          <h3>SKU 매트릭스</h3>
          <span>{formatNumber(rows.length)} SKU</span>
        </div>
        <Link className="text-button" to={`/products/barcodes?product=${encodeURIComponent(product.productKey)}`}>
          전체 바코드
        </Link>
      </div>
      <div className="table-frame">
        <table className="data-table product-sku-table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Readable alias</th>
              <th>바코드</th>
              <th>컬러</th>
              <th>사이즈</th>
              <th>실재고</th>
              <th>온라인 할당</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={String(row.sku || row.readableAlias)}>
                <td><strong>{row.sku || "-"}</strong></td>
                <td>{row.readableAlias || "-"}</td>
                <td>
                  {barcodeValue(row) ? (
                    <Link
                      className="barcode-chip"
                      aria-label={`${row.sku || row.readableAlias || "SKU"} 바코드 라벨`}
                      to={`/products/barcodes?sku=${encodeURIComponent(String(row.sku || ""))}`}
                    >
                      {barcodeValue(row)}
                    </Link>
                  ) : "미발급"}
                </td>
                <td>{row.colorwayName || row.colorwayCode || "-"}</td>
                <td>{formatSizeLabel(row.sizeName || row.sizeCode)}</td>
                <td><span className="num">{formatNumber(Number(row.onHandTotal || 0))}</span></td>
                <td><span className="num">{formatNumber(Number(row.allocatedTotal || 0))}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ProductContentPreview product={product} />
    </div>
  );
}

function ProductContentPreview({ product }: { product: ProductRow }) {
  const items = productContentReviewItems(product);
  return (
    <section className="products-content-preview" aria-label={`${product.productName} 상품 콘텐츠`}>
      <div className="products-sku-preview-head">
        <div>
          <h3>상품 콘텐츠</h3>
          <span>상세 본문, 사이즈 가이드, 상세 페이지 body 이미지</span>
        </div>
        <StatusBadge tone={productNeedsContentReview(product) ? "warning" : "success"}>
          {productNeedsContentReview(product) ? "확인 필요" : "준비됨"}
        </StatusBadge>
      </div>
      <div className="product-content-check-list">
        {items.map((item) => (
          <div className="product-content-check-row" key={item.key}>
            <div>
              <strong>{item.label}</strong>
              <span>{item.body}</span>
            </div>
            <StatusBadge tone={item.ok ? "success" : "warning"}>{item.ok ? "완료" : "필요"}</StatusBadge>
          </div>
        ))}
      </div>
    </section>
  );
}

function sortedSkuRows(rows: SkuRow[]): SkuRow[] {
  return [...rows].sort((a, b) =>
    String(a.colorwayName || a.colorwayCode || "").localeCompare(String(b.colorwayName || b.colorwayCode || ""), "ko") ||
    String(a.sizeName || a.sizeCode || "").localeCompare(String(b.sizeName || b.sizeCode || ""), "ko")
  );
}

function barcodeValue(row: SkuRow): string {
  return String(row.barcode || row.gtin || row.ean13 || row.ean || "").trim();
}

function productNeedsContentReview(product: ProductRow): boolean {
  return productContentReviewItems(product).some((item) => !item.ok);
}

function productContentReviewItems(product: ProductRow) {
  const source = product.source || {};
  return [
    {
      key: "product-image",
      label: "대표 상품 이미지",
      body: product.imageStatus === "이미지 있음" ? "상품 대표 이미지가 연결되어 있습니다." : "상품 목록과 상세에 사용할 대표 이미지가 필요합니다.",
      ok: product.imageStatus === "이미지 있음"
    },
    {
      key: "description",
      label: "상품 상세 본문",
      body: hasContentValue(source.descriptionHtml, source.detailHtml, source.description, source.contentBody)
        ? "상품 상세 본문이 준비되어 있습니다."
        : "상품 구매 결정에 필요한 상세 본문을 입력해야 합니다.",
      ok: hasContentValue(source.descriptionHtml, source.detailHtml, source.description, source.contentBody)
    },
    {
      key: "size-guide",
      label: "사이즈 가이드",
      body: hasContentValue(source.sizeGuide, source.sizeGuideHtml, source.measurementGuide)
        ? "사이즈 가이드가 준비되어 있습니다."
        : "사이즈 선택을 도울 가이드가 필요합니다.",
      ok: hasContentValue(source.sizeGuide, source.sizeGuideHtml, source.measurementGuide)
    },
    {
      key: "body-images",
      label: "상세 페이지 body 이미지",
      body: hasContentValue(source.detailPageBodyImages, source.bodyImages, source.contentImages)
        ? "상세 페이지 본문 이미지가 준비되어 있습니다."
        : "상품 이미지와 분리된 상세 페이지 body 이미지가 필요합니다.",
      ok: hasContentValue(source.detailPageBodyImages, source.bodyImages, source.contentImages)
    }
  ];
}

function hasContentValue(...values: unknown[]): boolean {
  return values.some((value) => Array.isArray(value) ? value.length > 0 : Boolean(String(value || "").trim()));
}

function parseOptionList(value: string): string[] {
  return value
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}
